/**
 * How long an instance was meant to live, and whether it has outlived that
 * (IM-134).
 *
 * IM-129 answers "how old is this". That cannot answer "was this supposed to
 * still be here": a Qrvey demo kept for a prospect legitimately runs for months,
 * and an alert that fires on it every day is an alert people mute — which is the
 * no-verdict rule's failure in different clothing.
 *
 * So the instance carries what it was *meant for*, stated by whoever created it,
 * and the screen reports a fact — "you asked for 7 days, it has been 34" —
 * rather than a judgement. That is what makes a warning defensible at all, and
 * it is the resolution of the standing no-cleanup-verdict rule rather than an
 * exception to it: nobody is guessing, the owner said so.
 *
 * ## "Over" is arithmetic and nothing else
 *
 * No threshold, no heuristic, and no default duration standing in for an unset
 * one. An instance with no lifetime recorded is **not** over; it is unset, which
 * is a different thing that prompts a different action.
 *
 * ## Nothing here destroys anything
 *
 * The obvious next suggestion and it is wrong. A false positive tears down a
 * real environment in an account somebody is working in, and destroy is this
 * product's one irreversible act — the only command that asks first (IM-79), and
 * the diagnostics role is denied `s3:GetObject` on the state bucket precisely so
 * a destroy cannot be undone from this side. The screen reports; a person
 * decides. That stays true when the list is long and the temptation strongest.
 */
export class InvalidLifetimeError extends Error {
}
/** The longest a duration may be asked for, in days. */
const MAX_DAYS = 3650;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Reads what a caller asked for, or refuses it.
 *
 * Shared so the console refuses before the request with the same wording — the
 * arrangement `deploymentIdProblem` and `validateRegistration` already use.
 *
 * Accepts `{ days }`, `{ until }` or `{ indefinite: true }` and exactly one of
 * them. Two at once is not a request that means anything, and picking one
 * silently is how somebody gets a lifetime they did not ask for.
 */
export const readLifetimeRequest = (input) => {
    if (typeof input !== 'object' || input === null || Array.isArray(input)) {
        throw new InvalidLifetimeError('a lifetime must be an object');
    }
    const body = input;
    const given = [body.days, body.until, body.indefinite].filter((v) => v !== undefined);
    if (given.length !== 1) {
        throw new InvalidLifetimeError('give exactly one of days, until or indefinite');
    }
    if (body.indefinite !== undefined) {
        if (body.indefinite !== true) {
            // `indefinite: false` is not "some other lifetime" — it is a caller
            // saying nothing, and answering it as a choice would record one.
            throw new InvalidLifetimeError('indefinite must be true, or give days or until instead');
        }
        return { kind: 'indefinite' };
    }
    if (body.days !== undefined) {
        const days = typeof body.days === 'number' ? body.days : Number.NaN;
        if (!Number.isInteger(days) || days < 1 || days > MAX_DAYS) {
            throw new InvalidLifetimeError(`days must be a whole number between 1 and ${String(MAX_DAYS)}, or say indefinite`);
        }
        return { kind: 'days', days };
    }
    const date = typeof body.until === 'string' ? body.until.trim() : '';
    if (!DATE_ONLY.test(date) || Number.isNaN(new Date(`${date}T00:00:00Z`).getTime())) {
        throw new InvalidLifetimeError('until must be a date, as YYYY-MM-DD');
    }
    return { kind: 'until', date };
};
/**
 * When a request means, as an instant.
 *
 * A duration is resolved against **now**, not against when the instance became
 * live: somebody saying "seven days" while looking at a month-old instance means
 * seven more days, and resolving from `liveSince` would mark it over the moment
 * they saved it.
 */
export const lifetimeExpiry = (asked, now) => {
    switch (asked.kind) {
        case 'indefinite':
            return null;
        case 'until':
            // End of the named day, so "until the 30th" includes the 30th.
            return `${asked.date}T23:59:59.999Z`;
        case 'days': {
            const at = new Date(now.getTime());
            at.setUTCDate(at.getUTCDate() + asked.days);
            return at.toISOString();
        }
    }
};
const DAY = 86_400_000;
/**
 * Where an instance stands against what it was meant for.
 *
 * Takes the mark rather than the instance, so it says nothing about age, status
 * or cost — and cannot start to. Whether an instance is *alerted* on is a
 * separate question (IM-135); this only reports.
 */
export const lifetimeState = (mark, now) => {
    if (mark === undefined)
        return { state: 'unset' };
    if (mark.expiresAt === null)
        return { state: 'indefinite', mark };
    const expires = new Date(mark.expiresAt).getTime();
    if (Number.isNaN(expires)) {
        // A mark whose instant cannot be read is not evidence of anything, and
        // reporting it as over would be a verdict drawn from a malformed record.
        return { state: 'unset' };
    }
    const difference = now.getTime() - expires;
    return difference > 0
        ? { state: 'over', mark, daysOver: Math.floor(difference / DAY) }
        : { state: 'within', mark, daysLeft: Math.ceil(-difference / DAY) };
};
/** How a request reads back, so a screen says what somebody asked for. */
export const describeLifetime = (asked) => {
    switch (asked.kind) {
        case 'indefinite':
            return 'indefinite';
        case 'until':
            return `until ${asked.date}`;
        case 'days':
            return asked.days === 1 ? '1 day' : `${String(asked.days)} days`;
    }
};
//# sourceMappingURL=lifetime.js.map